import React from "react";
import {Alert, Box, ButtonBase, Chip, Collapse, IconButton, Paper, Stack, TextField, Tooltip, Typography} from "@mui/material";
import Button from "@mui/material/Button";
import {AddOutlined, DeleteOutline, ExpandLess, ExpandMore, PlaylistAddCheckOutlined, RemoveCircleOutline} from "@mui/icons-material";
import {caseProblem} from "./formModel";
import {DlvCodeEditor} from "./DlvYamlEditor";
import {highlightAsp} from "./yamlHighlight";
import {MONOSPACE_FONT} from "./dlvStyles";

const ATOM_FIELD_SX = {'& input': {fontFamily: MONOSPACE_FONT, fontSize: 13}};

export const caseFieldId = (key, field) => `dlv-case-${key}-${field}`;

function focusLater(id) {
    setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
            element.focus();
            element.scrollIntoView({block: 'nearest'});
        }
    }, 0);
}

function FieldLabel({htmlFor, title, hint}) {
    return (
        <Box sx={{mb: 0.75}}>
            <Typography variant="subtitle2" component="label" htmlFor={htmlFor}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{hint}</Typography>
        </Box>
    );
}

function ExpectedAtoms({testCase, disabled, onChange}) {
    const {key, expected} = testCase;
    const setAtom = (index, value) => onChange({expected: expected.map((atom, i) => i === index ? value : atom)});
    const insertAfter = (index) => {
        onChange({expected: [...expected.slice(0, index + 1), '', ...expected.slice(index + 1)]});
        focusLater(caseFieldId(key, `expected-${index + 1}`));
    };
    const removeAt = (index) => onChange({expected: expected.filter((atom, i) => i !== index)});

    return (
        <Box>
            <FieldLabel
                htmlFor={caseFieldId(key, 'expected-0')}
                title="Atomi attesi"
                hint="Ognuno deve essere nell'answer set; se inizia con not deve mancare. Invio aggiunge un atomo."/>
            <Stack spacing={1}>
                {expected.map((atom, index) =>
                    <Stack key={index} direction="row" spacing={0.5} alignItems="center">
                        <TextField
                            id={caseFieldId(key, `expected-${index}`)}
                            size="small"
                            fullWidth
                            hiddenLabel
                            disabled={disabled}
                            value={atom}
                            placeholder="destinazione(p1, D)"
                            inputProps={{'aria-label': `Atomo atteso ${index + 1}`, spellCheck: false, autoCapitalize: 'off', autoComplete: 'off'}}
                            sx={ATOM_FIELD_SX}
                            onChange={(e) => setAtom(index, e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    insertAfter(index);
                                }
                            }}/>
                        <Tooltip title="Rimuovi atomo">
                            <span>
                                <IconButton size="small" aria-label={`Rimuovi l'atomo atteso ${index + 1}`}
                                            disabled={disabled || expected.length === 1}
                                            onClick={() => removeAt(index)}>
                                    <RemoveCircleOutline fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>)}
            </Stack>
            <Button size="small" startIcon={<AddOutlined/>} disabled={disabled} sx={{mt: 1, textTransform: 'none'}}
                    onClick={() => insertAfter(expected.length - 1)}>
                Aggiungi atomo
            </Button>
        </Box>
    );
}

function CaseCard({testCase, number, disabled, onChange, onToggle, onRemove}) {
    const {key, description, facts, open} = testCase;
    const problem = caseProblem(testCase);
    const atoms = testCase.expected.filter(atom => atom.trim() !== '').length;
    const bodyId = caseFieldId(key, 'body');

    const handleRemove = () => {
        const isEmpty = description.trim() === '' && facts.trim() === '' && atoms === 0;
        if (isEmpty || window.confirm(`Eliminare ${description.trim() ? `"${description.trim()}"` : `il caso ${number}`}?`)) {
            onRemove();
        }
    };

    return (
        <Paper variant="outlined" sx={{overflow: 'hidden'}}>
            <Stack direction="row" alignItems="center">
                <ButtonBase
                    onClick={onToggle}
                    aria-expanded={open}
                    aria-controls={bodyId}
                    sx={{flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', px: 2, py: 1.5, gap: 1.5}}>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography noWrap sx={{fontWeight: 600}}>
                            {description.trim() ? description : `Caso ${number}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {description.trim() ? `Caso ${number}, ` : ''}
                            {atoms} {atoms === 1 ? 'atomo atteso' : 'atomi attesi'}
                        </Typography>
                    </Box>
                    {problem && <Chip size="small" color="warning" variant="outlined" label={problem}/>}
                    {open ? <ExpandLess color="action"/> : <ExpandMore color="action"/>}
                </ButtonBase>
                <Tooltip title="Elimina test">
                    <span>
                        <IconButton aria-label={`Elimina il caso ${number}`} disabled={disabled} onClick={handleRemove}
                                    sx={{mr: 1}}>
                            <DeleteOutline/>
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Stack id={bodyId} spacing={2.5} sx={{px: 2, pb: 2.5, pt: 0.5}}>
                    <Box>
                        <FieldLabel htmlFor={caseFieldId(key, 'description')} title="Descrizione"
                                    hint="Cosa verifica questo test; compare nei risultati."/>
                        <TextField
                            id={caseFieldId(key, 'description')}
                            size="small"
                            fullWidth
                            hiddenLabel
                            disabled={disabled}
                            value={description}
                            onChange={(e) => onChange({description: e.target.value})}/>
                    </Box>
                    <Box>
                        <FieldLabel htmlFor={caseFieldId(key, 'facts')} title="Fatti"
                                    hint="I fatti del paziente in ASP, uno per riga."/>
                        <DlvCodeEditor
                            id={caseFieldId(key, 'facts')}
                            label={`Fatti del caso ${number}`}
                            value={facts}
                            onChange={(value) => onChange({facts: value})}
                            highlight={highlightAsp}
                            disabled={disabled}
                            placeholder="paziente(p1)."
                            minHeight={96}
                            maxHeight="320px"
                            padding={12}/>
                    </Box>
                    <ExpectedAtoms testCase={testCase} disabled={disabled} onChange={onChange}/>
                </Stack>
            </Collapse>
        </Paper>
    );
}

/**
 * Edits the tests with a field for each part, instead of the YAML. All the state lives in useFormCases.
 */
export function DlvTestsForm({form, disabled, onShowYaml, onAdd}) {
    const {cases, error, change, setOpen, remove} = form;

    if (error) {
        return (
            <Alert severity="warning"
                   action={<Button color="inherit" size="small" onClick={onShowYaml} sx={{textTransform: 'none'}}>
                       Modifica il YAML
                   </Button>}>
                {error}
            </Alert>
        );
    }

    if (cases.length === 0) {
        return (
            <Stack alignItems="center" spacing={1.5} sx={{py: 6, px: 3, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1, borderStyle: 'dashed'}}>
                <PlaylistAddCheckOutlined sx={{fontSize: 40, color: 'text.disabled'}}/>
                <Typography variant="h4" component="p">Nessun test</Typography>
                <Typography color="text.secondary" sx={{maxWidth: 360}}>
                    Aggiungi un test e compila descrizione, fatti e atomi attesi.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack spacing={1.5}>
            {cases.map((testCase, index) =>
                <CaseCard
                    key={testCase.key}
                    testCase={testCase}
                    number={index + 1}
                    disabled={disabled}
                    onChange={(values) => change(testCase.key, values)}
                    onToggle={() => setOpen(testCase.key, !testCase.open)}
                    onRemove={() => remove(testCase.key)}/>)}
        </Stack>
    );
}
